import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isReadOnly, type Role } from '@aithora/shared';
import { sx } from '../ui/designStyles';
import { MENU, ViewAsGroups } from './ViewAsPicker';
import { goToPhoneVersion, readForceDesktop } from '../phoneRedirect';
import { useLang } from '../i18n/LangContext';

/**
 * The top bar's role chip and the menu it opens (design T236-250, acctRows L37072-37077):
 * a Super Admin's View as rows first (the design's role picker groups), then the "Your
 * account" group: Notifications, Two-factor, My connections and Delete my account. A
 * Resident and an Aithora Home member see only Delete my account, at 60px / 19px. Delete my
 * account opens the dialog in the shell (DeleteAccountDialog), drawn for the signed-in role.
 */

const WRAP = 'position:relative;flex:0 0 auto;min-width:max-content;';
const CHIP = 'display:inline-flex;align-items:center;gap:8px;height:32px;padding:0 12px;border-top:1px solid rgba(34,39,46,.14);font-size:16px;font-weight:600;white-space:nowrap;text-decoration:none;color:#22272E;';
const RO = 'font-size:14px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#5B6B7C;background:rgba(91,107,124,.16);border-radius:4px;padding:2px 6px;';
const ACCOUNT_HEAD = 'font-size:14px;letter-spacing:.16em;text-transform:uppercase;color:#54606C;font-weight:600;padding:11px 14px 5px;border-top:1px solid rgba(34,39,46,.08);margin-top:4px;';

/** Roles whose account menu is the big-type one: only Delete my account. */
const BIG: ReadonlySet<Role> = new Set<Role>(['RESIDENT', 'MEMBER']);

export interface AccountItem {
  label: string;
  /** A console path, 'delete' for the Delete my account dialog, or 'phone' for the phone app. */
  to: string | 'delete' | 'phone';
}

/** The "Your account" rows for the signed-in role (your own role, even while viewing as). */
export function accountItems(realRole: Role | null, forceDesktop = readForceDesktop()): AccountItem[] {
  const out: AccountItem[] = [];
  if (!realRole || !BIG.has(realRole)) {
    out.push({ label: 'Notifications', to: '/settings?section=notifications' });
    out.push({ label: 'Two-factor', to: '/settings?section=two-factor' });
    out.push({ label: 'My connections', to: '/settings?section=my-connections' });
  }
  // A phone that chose the full console (?desktop=1) can go back to the phone app.
  if (forceDesktop) out.push({ label: 'Phone version', to: 'phone' });
  // Every role (Google Play and Apple require account deletion; Andrew 2026-10-02).
  out.push({ label: 'Delete my account', to: 'delete' });
  return out;
}

const rowStyle = (big: boolean, last: boolean) =>
  'display:flex;align-items:center;min-height:' + (big ? 60 : 36) + 'px;padding:0 14px;border-radius:7px;font-size:' + (big ? 19 : 16) + 'px;font-weight:600;text-decoration:none;color:' + (last ? '#8A1C16' : '#22272E') + ';';

export function AccountMenu({ chipText, realRole, viewingAs, onDeleteAccount, onSignOut }: {
  chipText: string;
  realRole: Role | null;
  viewingAs: Role | null;
  onDeleteAccount?: () => void;
  /** A phone frame with no side nav has no other Sign out, so the menu carries it. */
  onSignOut?: () => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const superAdmin = realRole === 'SUPER_ADMIN';
  const big = !!realRole && BIG.has(realRole);
  const items = accountItems(realRole);
  const run = (it: AccountItem) => {
    setOpen(false);
    if (it.to === 'phone') {
      goToPhoneVersion();
    } else if (it.to === 'delete') {
      if (onDeleteAccount) onDeleteAccount();
      else navigate('/delete-account');
    } else navigate(it.to);
  };

  return (
    <div ref={ref} style={sx(WRAP)}>
      <button
        type="button"
        className="dtab viewas__chip"
        style={sx(CHIP)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Signed in as ${chipText}. Your account${superAdmin ? ', and View the console as another role' : ''}`}
        data-testid="role-chip"
        onClick={() => setOpen((o) => !o)}
      >
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} aria-hidden="true" />
        {chipText}
        {isReadOnly(realRole) && <span style={sx(RO)}>Read only</span>}
        <span style={sx('color:#54606C;')} aria-hidden="true">
          {'▾'}
        </span>
      </button>
      {open && (
        <div role="menu" aria-label={superAdmin ? 'View as' : 'Your account'} style={sx(MENU)}>
          {superAdmin && <ViewAsGroups viewingAs={viewingAs} onPicked={() => setOpen(false)} />}
          <div role="group" aria-label="Your account" style={sx('display:flex;flex-direction:column;')}>
            <div style={sx(ACCOUNT_HEAD)}>Your account</div>
            {items.map((it, i) => (
              <button key={it.label} type="button" role="menuitem" className="dtab viewas__item" style={sx(rowStyle(big, i === items.length - 1))} onClick={() => run(it)}>
                {it.label}
              </button>
            ))}
            {onSignOut && (
              <button type="button" role="menuitem" className="dtab viewas__item" style={sx(rowStyle(big, false))} onClick={() => { setOpen(false); onSignOut(); }}>
                Sign out
              </button>
            )}
          </div>
          <MenuLanguage />
        </div>
      )}
    </div>
  );
}

/** English and Español, the names each language calls itself (never translated). */
const LANG_OPTS: ['en' | 'es', string][] = [
  ['en', 'English'],
  ['es', 'Español'],
];
const langBtn = (on: boolean) =>
  'display:inline-flex;align-items:center;min-height:34px;padding:0 12px;border-radius:7px;font-family:inherit;font-size:16px;font-weight:700;text-decoration:none;cursor:pointer;' +
  (on ? 'background:#264C7E;color:#FFFFFF;border:1px solid #264C7E;' : 'background:transparent;border:1px solid rgba(34,39,46,.22);color:#22272E;');

/**
 * V9 account menu (design "Account · Language", L755): Language, English / Español. The same
 * pick as Settings > Channels > Language (LangContext): kept on the account, or on this
 * device while viewing as another role.
 */
export function MenuLanguage() {
  const { lang, setLang } = useLang();
  return (
    <div data-screen-label="Account · Language" role="radiogroup" aria-label="Language" data-testid="menu-language" style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;padding:6px 14px 8px;')}>
      <span style={sx('flex:1 1 auto;font-size:16px;color:#22272E;')}>Language</span>
      {LANG_OPTS.map(([code, label]) => (
        <button key={code} type="button" role="radio" aria-checked={lang === code} lang={code} translate="no" data-testid={`menu-lang-${code}`} onClick={() => setLang(code)} style={sx(langBtn(lang === code))}>
          {label}
        </button>
      ))}
    </div>
  );
}
